import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { PlusIcon } from '@heroicons/react/24/outline';
import { domainsApi } from '@/features/domains/api';
import { useAuthStore } from '@/stores/authStore';
/**
 * SSL-scoped dashboard — the landing page at /products/ssl/dashboard.
 *
 * Wave 0 scaffold: pulls the same domain list the Domains page uses and
 * derives KPIs client-side (Total / Healthy / Warn / Critical / Expiring
 * in 30 days). Wave 1 will move the derivation to a dedicated backend
 * endpoint and add a recent-activity feed plus a top-5-expiring table.
 *
 * Intentionally simple — the goal here is to prove the ProductShell
 * navigation + accent colour story, not to ship the final dashboard.
 */
export function SslDashboardPage() {
    const orgId = useAuthStore((s) => s.user?.orgId);
    const { data: domains = [], isLoading } = useQuery({
        queryKey: ['ssl', 'domains', orgId],
        queryFn: () => domainsApi.list(String(orgId ?? '')),
        enabled: !!orgId,
    });
    const kpis = deriveKpis(domains);
    return (_jsxs("div", { className: "space-y-8", children: [_jsxs("header", { className: "flex items-end justify-between gap-6", children: [_jsxs("div", { className: "space-y-1", children: [_jsx("div", { className: "text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-500)]", style: { fontFamily: 'var(--f-mono)' }, children: "Home / SSL Monitor" }), _jsxs("h1", { className: "text-[34px] font-normal leading-[1.1] text-[var(--ink-900)]", style: { fontFamily: 'var(--f-display)' }, children: ["SSL ", _jsx("em", { children: "dashboard" })] }), _jsx("p", { className: "text-sm text-[var(--ink-500)]", children: isLoading
                                    ? 'Loading domains…'
                                    : `${kpis.total} tracked · ${kpis.healthy} healthy · ${kpis.warn} warnings · ${kpis.critical} critical · ${kpis.expired} expired.` })] }), _jsxs(Link, { to: "/products/ssl/domains/new", className: "inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-semibold text-white no-underline shadow-[0_6px_18px_-4px_rgba(91,63,228,0.55)] transition-[transform,box-shadow] hover:-translate-y-px", style: {
                            background: 'linear-gradient(180deg, var(--royal-hi), var(--royal))',
                        }, children: [_jsx(PlusIcon, { className: "h-4 w-4", "aria-hidden": true }), "Add domain"] })] }), _jsxs("section", { className: "grid grid-cols-2 gap-4 md:grid-cols-5", children: [_jsx(KpiCard, { label: "Tracked", value: kpis.total, tone: "neutral" }), _jsx(KpiCard, { label: "Healthy", value: kpis.healthy, tone: "good" }), _jsx(KpiCard, { label: "Warn", value: kpis.warn, tone: "warn" }), _jsx(KpiCard, { label: "Critical", value: kpis.critical, tone: "bad" }), _jsx(KpiCard, { label: "Expired", value: kpis.expired, tone: "bad" })] }), _jsxs("section", { className: "surface-card p-6", children: [_jsxs("h2", { className: "mb-2 text-xl text-[var(--ink-900)]", style: { fontFamily: 'var(--f-display)' }, children: ["Top expiring ", _jsx("em", { children: "soon" })] }), _jsxs("p", { className: "text-sm text-[var(--ink-500)]", children: ["Wave 1 wires a dedicated endpoint; for now see the full list on the", ' ', _jsx(Link, { to: "/products/ssl/domains", className: "text-[var(--royal-text)]", children: "Domains" }), ' ', "page."] })] })] }));
}
function KpiCard({ label, value, tone, }) {
    const toneColor = {
        neutral: 'var(--ink-900)',
        good: 'var(--emerald)',
        warn: 'var(--gold)',
        bad: 'var(--crimson)',
    }[tone];
    return (_jsxs("div", { className: "surface-card p-5", children: [_jsx("div", { className: "text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-500)]", style: { fontFamily: 'var(--f-mono)' }, children: label }), _jsx("div", { className: "mt-2 text-3xl font-semibold leading-none", style: { color: toneColor }, children: value })] }));
}
function deriveKpis(domains) {
    const total = domains.length;
    let healthy = 0;
    let warn = 0;
    let critical = 0;
    let expired = 0;
    for (const d of domains) {
        const status = (d.status ?? '').toLowerCase();
        // QA Raji #23 — an expired cert (past its notAfter) is its OWN state: it is
        // neither Critical nor a Warning. It gets its own KPI.
        const isExpired = status === 'expired' ||
            ((status === 'red' || status === 'critical') &&
                d.daysToExpire != null &&
                d.daysToExpire < 0);
        if (isExpired)
            expired += 1;
        else if (status === 'healthy' || status === 'green')
            healthy += 1;
        else if (status === 'warn' || status === 'yellow' || status === 'warning')
            warn += 1;
        else if (status === 'critical' || status === 'red')
            critical += 1;
    }
    return { total, healthy, warn, critical, expired };
}
